// Import form page — 3-step wizard: supplier → items → preview+commit.
// Route: /inventory/imports/new  and  /inventory/imports/:id
import { useState } from "react";
import { useNavigate, useParams } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/lib/api/client";
import { Button } from "@/components/ui/button";
import { ImportFormStepOne, type Step1Data } from "./import-form-step-one";
import { ImportFormStepTwo } from "./import-form-step-two";

interface StockImport {
  id: string;
  code: string;
  status: string;
  supplierName: string | null;
  warehouseId: string;
  warehouseName: string | null;
  totalCost: string;
  note: string;
  createdAt: string;
  committedAt: string | null;
}

interface ImportDetail {
  import: StockImport;
  items: Array<{
    id: string;
    productName: string | null;
    variantSku: string | null;
    quantity: number;
    unitCost: string;
    serials: string[];
  }>;
}

function fmtCost(cost: string) {
  const n = parseFloat(cost);
  if (isNaN(n)) return "0 ₫";
  return new Intl.NumberFormat("vi-VN", { style: "currency", currency: "VND" }).format(n);
}

const STEPS = ["Thông tin", "Thêm hàng", "Xác nhận"];

export function ImportFormPage() {
  const params = useParams({ strict: false }) as { id?: string };
  const navigate = useNavigate();
  const qc = useQueryClient();

  const [step, setStep] = useState(params.id ? 1 : 0);
  const [importId, setImportId] = useState<string | null>(params.id ?? null);
  const [step1, setStep1] = useState<Step1Data>({ supplierId: "", warehouseId: "", note: "" });

  // Load existing import detail when on step 2/3
  const { data: detail } = useQuery({
    queryKey: ["import-detail", importId],
    queryFn: () =>
      apiClient
        .get<{ data: ImportDetail }>(`/v1/imports/${importId}`)
        .then((r) => r.data.data),
    enabled: !!importId,
  });

  const si = detail?.import;
  const items = detail?.items ?? [];
  const isDraft = !si || si.status === "draft";

  // Step 0 → create draft
  const createMutation = useMutation({
    mutationFn: (body: object) =>
      apiClient.post<{ data: StockImport }>("/v1/imports", body).then((r) => r.data.data),
    onSuccess: (created) => {
      setImportId(created.id);
      qc.invalidateQueries({ queryKey: ["imports"] });
      setStep(1);
      void navigate({ to: "/inventory/imports/$id", params: { id: created.id } });
    },
  });

  // Step 2 → commit
  const commitMutation = useMutation({
    mutationFn: () =>
      apiClient.post(`/v1/imports/${importId}/commit`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["imports"] });
      qc.invalidateQueries({ queryKey: ["import-detail", importId] });
      qc.invalidateQueries({ queryKey: ["stock"] });
    },
  });

  function handleCreateDraft() {
    createMutation.mutate({
      supplierId: step1.supplierId || null,
      warehouseId: step1.warehouseId,
      note: step1.note,
    });
  }

  const totalPreview = items.reduce((acc, item) => {
    return acc + (parseFloat(item.unitCost) || 0) * item.quantity;
  }, 0);

  return (
    <div className="space-y-6 max-w-3xl">
      <h1 className="text-2xl font-semibold">
        {importId ? `Phiếu nhập ${si?.code ?? "…"}` : "Tạo phiếu nhập"}
      </h1>

      {/* Step indicator */}
      <div className="flex gap-2">
        {STEPS.map((label, i) => (
          <div
            key={i}
            className={`flex items-center gap-2 text-sm ${i === step ? "font-semibold text-primary" : "text-muted-foreground"}`}
          >
            <span className={`flex h-6 w-6 items-center justify-center rounded-full text-xs
              ${i === step ? "bg-primary text-primary-foreground" : i < step ? "bg-green-500 text-white" : "bg-muted"}`}>
              {i < step ? "✓" : i + 1}
            </span>
            {label}
            {i < STEPS.length - 1 && <span className="text-muted-foreground">›</span>}
          </div>
        ))}
      </div>

      {/* Step 0: Supplier + note */}
      {step === 0 && (
        <div className="space-y-4">
          <ImportFormStepOne data={step1} onChange={setStep1} />
          <Button
            onClick={handleCreateDraft}
            disabled={createMutation.isPending || !step1.warehouseId}
          >
            {createMutation.isPending ? "Đang tạo…" : "Tiếp theo →"}
          </Button>
        </div>
      )}

      {/* Step 1: Add items */}
      {step === 1 && importId && isDraft && (
        <div className="space-y-4">
          <ImportFormStepTwo importId={importId} />
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setStep(0)}>← Quay lại</Button>
            <Button onClick={() => setStep(2)} disabled={items.length === 0}>
              Xem trước →
            </Button>
          </div>
        </div>
      )}

      {/* Step 2: Preview + commit */}
      {step === 2 && si && (
        <div className="space-y-4">
          <div className="rounded-md border p-4 space-y-2 bg-muted/20">
            <p className="text-sm"><span className="text-muted-foreground">Mã phiếu:</span> {si.code}</p>
            <p className="text-sm"><span className="text-muted-foreground">Kho nhập:</span> {si.warehouseName ?? "—"}</p>
            <p className="text-sm"><span className="text-muted-foreground">Nhà cung cấp:</span> {si.supplierName ?? "—"}</p>
            <p className="text-sm"><span className="text-muted-foreground">Số mặt hàng:</span> {items.length}</p>
            <p className="text-sm font-semibold">
              Tổng tiền: {fmtCost(String(totalPreview))}
            </p>
          </div>

          {si.status === "committed" ? (
            <p className="text-green-700 font-medium">Phiếu đã được nhập thành công.</p>
          ) : (
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setStep(1)}>← Sửa</Button>
              <Button
                onClick={() => commitMutation.mutate()}
                disabled={commitMutation.isPending || items.length === 0}
              >
                {commitMutation.isPending ? "Đang xử lý…" : "Xác nhận nhập hàng"}
              </Button>
            </div>
          )}

          {commitMutation.isSuccess && (
            <p className="text-green-700">Nhập kho thành công! Tồn kho đã được cập nhật.</p>
          )}
          {commitMutation.isError && (
            <p className="text-destructive">Có lỗi xảy ra. Vui lòng thử lại.</p>
          )}
        </div>
      )}
    </div>
  );
}
